import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";

import { WidgetScreen } from "../../common/WidgetScreen";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useWidgetAddSignalOverviewPreviewQuery } from "./useWidgetAddSignalOverviewPreviewQuery";
import { WASignalOverviewMutation } from "./__generated__/WASignalOverviewMutation.graphql";
import { WASignalOverviewQuery } from "./__generated__/WASignalOverviewQuery.graphql";

import { WidgetAddSignalOverviewPreview } from "./WidgetAddSignalOverviewPreview";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddSignals">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddSignals">;
};

export const WASignalOverview = withWebModal(WASignalOverviewC, true);
function WASignalOverviewC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWASignalOverview {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

const WA_SIGNAL_OVERVIEW_QUERY = graphql`
  query WASignalOverviewQuery {
    store {
      ...WidgetOverviewSettings_Store
    }
  }
`;

function InnerWASignalOverview(props: Props & { fetchKey: string }) {
  const [teamIds, setTeamIds] = useState(props.route.params.defaultTeams);
  const data = useLazyLoadQuery<WASignalOverviewQuery>(
    WA_SIGNAL_OVERVIEW_QUERY,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const query = useWidgetAddSignalOverviewPreviewQuery({
    teamIds,
    storecastId: props.route.params.storecastId,
  });

  const [createWidgetSignalOverview, loading] = useMutation<WASignalOverviewMutation>(
    graphql`
      mutation WASignalOverviewMutation(
        $storecastId: ID!
        $input: CreateWidgetSignalOverviewInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetSignalOverview(widget: $input) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      const position = props.route.params.position;
      createWidgetSignalOverview({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX: position.x + position.c * 2,
            positionY: position.y,
            size: WAWidgetSizeToOverviewSize(size),
            teamIds,
          },
        },
        onError: (e) => {
          console.log("e", e);
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
      });
    },
    [
      createWidgetSignalOverview,
      props.navigation,
      props.route.params.position,
      props.route.params.storecastId,
      teamIds,
    ]
  );

  const renderPreview = useCallback(
    (size: WidgetSizeT) =>
      query ? <WidgetAddSignalOverviewPreview query={query} /> : null,
    [query]
  );

  return (
    <WidgetScreen
      preview={
        <WASizePreview
          onCreate={onCreate}
          loading={loading}
          unitSize={props.route.params.unitSize}
          maxSize={props.route.params.maxSize}
          widgetSizes={overviewWidgetSizes}
        >
          {renderPreview}
        </WASizePreview>
      }
      settings={
        <WidgetOverviewSettings
          store={data.store}
          teamIds={teamIds}
          setTeamIds={setTeamIds}
        />
      }
    />
  );
}
